import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'

export type VacationType =
  | 'WYPOCZYNKOWY'
  | 'NA_ZADANIE'
  | 'BEZPLATNY'
  | 'CHOROBOWY'
  | 'OPIEKA_NAD_DZIECKIEM'
export type VacationStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED'

export interface VacationRequest {
  id: string
  userId: string
  type: VacationType
  status: VacationStatus
  startDate: string // YYYY-MM-DD
  endDate: string
  workingDays: number
  comment: string | null
  decisionNote: string | null
  decidedAt: string | null
  decidedBy: { id: string; name: string } | null
  createdAt: string
}

export interface VacationBalanceByType {
  WYPOCZYNKOWY: { approvedDays: number; pendingDays: number }
  NA_ZADANIE: {
    approvedDays: number
    pendingDays: number
    maxPerYear: number
    remainingPerYear: number
  }
  BEZPLATNY: { approvedDays: number }
  CHOROBOWY: { approvedDays: number }
  OPIEKA_NAD_DZIECKIEM: {
    approvedDays: number
    pendingDays: number
    maxPerYear: number
    remainingPerYear: number
  }
}

export interface VacationBalanceResponse {
  year: number
  balance: {
    totalDays: number
    usedDays: number
    pendingDays: number
    remainingDays: number
  }
  byType: VacationBalanceByType
}

export const VACATION_KEY = ['vacation'] as const

export function useMyVacationBalance(year?: number) {
  return useQuery({
    queryKey: [...VACATION_KEY, 'balance', year ?? 'current'],
    queryFn: () => {
      const qs = year !== undefined ? `?year=${year}` : ''
      return apiFetch<VacationBalanceResponse>(`/api/vacation/balance${qs}`)
    },
    staleTime: 30_000,
  })
}

export interface MyRequestsFilter {
  status?: VacationStatus
  year?: number
}

export function useMyVacationRequests(filters?: MyRequestsFilter) {
  return useQuery({
    queryKey: [...VACATION_KEY, 'requests', filters ?? null],
    queryFn: () => {
      const qs = new URLSearchParams()
      if (filters?.status) qs.set('status', filters.status)
      if (filters?.year !== undefined) qs.set('year', String(filters.year))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return apiFetch<{ requests: VacationRequest[] }>(`/api/vacation/requests${suffix}`)
    },
    staleTime: 30_000,
  })
}

export interface CreateVacationRequestBody {
  type: 'WYPOCZYNKOWY' | 'NA_ZADANIE' | 'BEZPLATNY' | 'OPIEKA_NAD_DZIECKIEM'
  startDate: string
  endDate: string
  comment?: string
}

export function useCreateMyVacationRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateVacationRequestBody) =>
      apiFetch<{ request: VacationRequest }>('/api/vacation/requests', {
        method: 'POST',
        body: { ...body },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VACATION_KEY })
      qc.invalidateQueries({ queryKey: ['admin', 'vacation'] })
      // Karta progresu zalicza dni urlopu jako godziny — anulowanie albo
      // złożenie wniosku zmienia jej wynik od razu, nie po refetchInterval.
      qc.invalidateQueries({ queryKey: ['my-monthly-progress'] })
      qc.invalidateQueries({ queryKey: ['admin', 'reports'] })
    },
  })
}

export function useCancelMyVacationRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ request: VacationRequest }>(
        `/api/vacation/requests/${id}/cancel`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VACATION_KEY })
      qc.invalidateQueries({ queryKey: ['admin', 'vacation'] })
      // Karta progresu zalicza dni urlopu jako godziny — anulowanie albo
      // złożenie wniosku zmienia jej wynik od razu, nie po refetchInterval.
      qc.invalidateQueries({ queryKey: ['my-monthly-progress'] })
      qc.invalidateQueries({ queryKey: ['admin', 'reports'] })
    },
  })
}
